import classNames from 'classnames';
import { useContext, useEffect, useRef } from 'react';
import { formatTaskDateTimeWithLocalTime } from '@/modules/common/helpers/taskDateTime.ts';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import TaskResultInfoModalWithLoading from '@/modules/admin-dashboard/components/modals/TaskResultInfoModalWithLoading.tsx';
import {
  useDeleteTaskResultMutation,
  useRestoreTaskResultMutation,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { Avatar, Button, ConfirmModal, ModalProviderContext, TooltipNew } from 'ui';
import {
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
  TaskResult,
  TaskResultSchema,
  TaskResultSummaryResponse,
  User,
} from 'compliance-api';

interface TaskItemResultInfoProps {
  user: User;
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse;
  result: TaskResultSchema | TaskResultSummaryResponse | null;
  openFromUrl?: boolean;
  onOpenFromUrl?: () => void;
}

const TaskItemResultInfo = ({
  user,
  result,
  task,
  openFromUrl,
  onOpenFromUrl,
}: TaskItemResultInfoProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: deleteResult } = useDeleteTaskResultMutation();
  const hasOpenedModal = useRef(false);
  const dueDate = result && 'due_date' in result ? result.due_date : null;
  const completedAtDateTime = formatTaskDateTimeWithLocalTime(result?.completion_date);
  const dueDateTime = formatTaskDateTimeWithLocalTime(dueDate);

  useEffect(() => {
    if (openFromUrl && result && !hasOpenedModal.current) {
      openModal(TaskResultInfoModalWithLoading, {
        task,
        user,
        resultId: result.id,
      });
      hasOpenedModal.current = true;
      if (onOpenFromUrl) {
        onOpenFromUrl();
      }
    }
  }, [openFromUrl]);

  return (
    <div
      className="flex items-center justify-between [&:not(:last-child)]:border-b py-4 cursor-pointer group"
      onClick={
        result && result.result === TaskResult.Completed
          ? () => {
              openModal(TaskResultInfoModalWithLoading, {
                task,
                user,
                resultId: result.id,
              });
            }
          : undefined
      }
    >
      <div className="flex items-center gap-4">
        <Avatar src={user.avatar_url} firstName={user.first_name} lastName={user.last_name} />
        <span
          className={classNames('w-[150px]', {
            'group-hover:underline': result && result.result === TaskResult.Completed,
          })}
        >
          {user.first_name} {user.last_name}
        </span>
        <div className="flex flex-col gap-2 w-[140px]">
          <div className="text-[12px] text-gray-500">ID</div>
          <div className="capitalize">{result ? result.reference_name : '---'}</div>
        </div>
        <div className="flex flex-col gap-2 w-[100px]">
          <div className="text-[12px] text-gray-500">Result</div>
          <div className="capitalize">{result ? result.result : '---'}</div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-[12px] text-gray-500">
            {result?.result === TaskResult.Due ? 'Overdue ' : 'Completed '}at
          </div>
          <div>
            <div>{completedAtDateTime.taskDateTime}</div>
            {completedAtDateTime.localDateTime && (
              <div className="text-xs text-gray-500">
                local time: {completedAtDateTime.localDateTime}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 w-[180px] ml-4">
          <div className="text-[12px] text-gray-500">Due Date</div>
          <div className="whitespace-nowrap">
            <div>{dueDateTime.taskDateTime}</div>
            {dueDateTime.localDateTime && (
              <div className="text-xs text-gray-500">local time: {dueDateTime.localDateTime}</div>
            )}
          </div>
        </div>
      </div>
      {result && result.deleted_at && (
        <ArchivedItemAction id={result?.id || 0} mutation={useRestoreTaskResultMutation} />
      )}
      {result && !result?.deleted_at && result && (
        <TooltipNew
          text="You can only delete completed task results."
          disabled={result.result === TaskResult.Completed}
        >
          <Button
            variant="plain"
            className="w-fit"
            disabled={result.result !== TaskResult.Completed}
            onClick={e => {
              e.stopPropagation();
              openModal(ConfirmModal, {
                title: `Are you sure you want to archive the result of the task "${task.name}" performed by ${user.first_name} ${user.last_name}?`,
                onConfirm: async () => {
                  deleteResult({
                    id: result?.id,
                  });
                },
              });
            }}
          >
            Archive
          </Button>
        </TooltipNew>
      )}
    </div>
  );
};

export default TaskItemResultInfo;
